import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('[ErrorBoundary]', error, errorInfo);
    }

    render(): ReactNode {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
                    <div className="text-center max-w-md">
                        <div className="w-16 h-16 rounded-lg bg-[#FEE2E2] flex items-center justify-center mx-auto mb-6">
                            <span className="text-3xl">!</span>
                        </div>
                        <h1 className="text-2xl font-bold text-[#1E293B] mb-2">
                            Une erreur est survenue
                        </h1>
                        <p className="text-[#64748B] mb-6">
                            L'application a rencontré un problème inattendu. Veuillez recharger la page.
                        </p>
                        {import.meta.env.DEV && this.state.error && (
                            <pre className="text-left text-xs text-[#EF4444] bg-[#FEF2F2] p-3 rounded-lg mb-6 overflow-auto max-h-32">
                                {this.state.error.message}
                            </pre>
                        )}
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-[#0F2744] text-white rounded-lg hover:bg-[#091A2E] transition-colors"
                            >
                                Recharger la page
                            </button>
                            <a
                                href="/dashboard"
                                className="px-4 py-2 border border-[#E2E8F0] rounded-lg text-[#64748B] hover:bg-[#F1F5F9] transition-colors"
                            >
                                Retour au dashboard
                            </a>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
